import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';

interface DocumentChatProps {
  documentId: number;
  documentTitle: string;
}

interface ChatMessage {
  question: string;
  answer: string;
}

const DocumentChat: React.FC<DocumentChatProps> = ({ documentId, documentTitle }) => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAskQuestion = async () => {
    if (!question.trim()) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${window.location.origin}/api/documents/${documentId}/chat/`,
        { question },
        { headers: { Authorization: `Token ${token}` } }
      );

      setMessages([...messages, {
        question: response.data.question,
        answer: response.data.answer
      }]);
      setQuestion('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Senden der Frage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Chat mit: {documentTitle}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2, maxHeight: 400, overflowY: 'auto', bgcolor: '#f5f5f5' }}>
        {messages.length === 0 ? (
          <Typography color="text.secondary" align="center">
            Stelle eine Frage zum Dokument...
          </Typography>
        ) : (
          messages.map((msg, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Paper sx={{ p: 1.5, mb: 1, bgcolor: '#e3f2fd' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Frage:
                </Typography>
                <Typography variant="body2">{msg.question}</Typography>
              </Paper>
              <Paper sx={{ p: 1.5, bgcolor: '#fff' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Antwort:
                </Typography>
                <Typography variant="body2">{msg.answer}</Typography>
              </Paper>
            </Box>
          ))
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Frage zum Dokument stellen..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAskQuestion()}
          disabled={loading}
        />
        <Button
          variant="contained"
          onClick={handleAskQuestion}
          disabled={loading || !question.trim()}
          endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          Senden
        </Button>
      </Box>
    </Box>
  );
};

export default DocumentChat;
